import { z } from 'zod'
import { envelopeStatusSchema } from '../enums/envelope-status.enum.js'
import { tenantModeSchema } from '../enums/tenant-mode.enum.js'
import { questionRoleSchema } from '../enums/question-role.enum.js'
import { signingSessionGateSchema, type SigningSessionGate } from '../enums/signing-session-gate.enum.js'
import { signerStatusSchema } from '../enums/signer-status.enum.js'
import { signerDeliverySchema } from '../enums/signer-delivery.enum.js'
import { originUrlSchema } from '../helpers/json.js'
import { brandDtoSchema } from './brand.dto.js'
import { documentDtoSchema, fieldDtoSchema } from './envelope.dto.js'
import {
  presenceViewerDtoSchema,
  questionThreadDtoSchema,
} from './question.dto.js'
import { uuidSchema } from '../validators/params.js'
import { knownTimeZoneSchema } from '../validators/tenant.validator.js'

export { signingSessionGateSchema, type SigningSessionGate }

const signingSessionShared = {
  token: z.string().min(1),
  consentVersion: z.string(),
  consentText: z.string(),
  envelope: z.object({
    id: uuidSchema,
    title: z.string(),
    status: envelopeStatusSchema,
    expiresAt: z.string().datetime(),
    /** `test` pages say "Test envelope: not legally binding". */
    mode: tenantModeSchema,
  }),
  brand: brandDtoSchema,
  /**
   * The organization's time zone (IANA). A date-signed field shows today's
   * date there, which is the date the server stamps when the signer finishes.
   */
  timeZone: knownTimeZoneSchema,
  signer: z.object({
    id: uuidSchema,
    name: z.string(),
    email: z.string().email(),
    role: z.string(),
    status: signerStatusSchema,
    consentAcceptedAt: z.string().datetime().nullable(),
    redirectUrl: z.string().url().nullable(),
    /** `caller` when the sender delivers this signer's link (signed in to their app), `email` otherwise. */
    delivery: signerDeliverySchema,
    /**
     * Which save of this signer's draft the fields' values are; a draft save
     * names it (see `saveSigningDraftInputSchema`). This API always sends it.
     * Optional so a page deployed before the API that sends it still loads.
     */
    draftRevision: z.number().int().nonnegative().optional(),
    /** When they last asked the organization for a paper copy; a page doesn't offer another for a day. */
    paperCopyRequestedAt: z.string().datetime().nullable().optional(),
  }),
  embedOrigins: z.array(originUrlSchema),
  /**
   * What the organization charges for a paper copy (0 is free), for the
   * request dialog. This API always sends it. Optional so a page deployed
   * before the API that takes paper-copy requests still loads; the page offers
   * the request only when it's present.
   */
  paperCopyFeeMicros: z.number().int().optional(),
  /**
   * Whether this signer has signature and initials fields to sign now (both
   * false until it's their turn), sent from the consent step on, so a signer
   * the sender delivers can adopt a signature there instead of in a step of
   * its own.
   */
  inkFields: z.object({ signature: z.boolean(), initials: z.boolean() }),
}

const emptyDocuments = z.array(documentDtoSchema).max(0)
const emptyFields = z.array(fieldDtoSchema).max(0)
const emptyThreads = z.array(questionThreadDtoSchema).max(0)
const emptyViewers = z.array(presenceViewerDtoSchema).max(0)

/**
 * The signers on the envelope who have signed, so the page can show a field
 * they left empty as empty rather than as waiting for them.
 */
const signedSignerIds = z.array(uuidSchema)

const questionSessionFields = {
  canSign: z.boolean(),
  questionsEnabled: z.boolean(),
  questionRole: questionRoleSchema.nullable(),
  threads: z.array(questionThreadDtoSchema),
  viewers: z.array(presenceViewerDtoSchema),
}

export const signingSessionDtoSchema = z.discriminatedUnion('gate', [
  z.object({
    ...signingSessionShared,
    gate: z.literal('consent' satisfies SigningSessionGate),
    unavailableReason: z.null(),
    documents: emptyDocuments,
    fields: emptyFields,
    canSign: z.literal(false),
    questionsEnabled: z.boolean(),
    questionRole: questionRoleSchema.nullable(),
    threads: emptyThreads,
    viewers: emptyViewers,
  }),
  z.object({
    ...signingSessionShared,
    gate: z.literal('waiting' satisfies SigningSessionGate),
    unavailableReason: z.null(),
    documents: emptyDocuments,
    fields: emptyFields,
    canSign: z.literal(false),
    questionsEnabled: z.boolean(),
    questionRole: questionRoleSchema.nullable(),
    threads: emptyThreads,
    viewers: emptyViewers,
  }),
  z.object({
    ...signingSessionShared,
    gate: z.literal('ready' satisfies SigningSessionGate),
    unavailableReason: z.null(),
    documents: z.array(documentDtoSchema),
    fields: z.array(fieldDtoSchema),
    signedSignerIds,
    ...questionSessionFields,
  }),
  z.object({
    ...signingSessionShared,
    gate: z.literal('signed' satisfies SigningSessionGate),
    unavailableReason: z.null(),
    documents: z.array(documentDtoSchema),
    fields: z.array(fieldDtoSchema),
    signedSignerIds,
    ...questionSessionFields,
  }),
  z.object({
    ...signingSessionShared,
    gate: z.literal('unavailable' satisfies SigningSessionGate),
    unavailableReason: z.string(),
    documents: emptyDocuments,
    fields: emptyFields,
    canSign: z.literal(false),
    questionsEnabled: z.boolean(),
    questionRole: questionRoleSchema.nullable(),
    threads: emptyThreads,
    viewers: emptyViewers,
  }),
])

export type SigningSessionDto = z.infer<typeof signingSessionDtoSchema>
